import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Tabs, Form, Modal, Input, Select, Row, Col } from "antd";
import dayjs from "dayjs";
import PageLayout from "../../components/layout/PageLayout";
import collectionsService from "../../services/collectionsService";
import announcementService from "../../services/announcementService";
import spotlightService from "../../services/spotlightService";
import ImageUpload from "../../components/banners/ImageUpload";
import masterService from "../../services/masterService";
import { useUser } from "../../context/UserContext";
import { useNotify } from "../../context/NotificationContext";
import CollectionDetailsForm from "../../components/collections/CollectionDetailsForm";
import CollectionFiltersForm from "../../components/collections/CollectionFiltersForm";
import CollectionSeoForm from "../../components/collections/CollectionSeoForm";
import PinnedManager from "../../components/collections/PinnedManager";
import { useCommon } from "../../context/CommonContext";

const parseNaiveDateTime = (value) => {
  if (!value) return undefined;
  if (dayjs.isDayjs(value)) return value;
  const s = (value instanceof Date ? value.toISOString() : String(value)).trim();
  if (!s) return undefined;
  // Extract only YYYY-MM-DD — never convert timezone
  const match = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? dayjs(match[1]) : dayjs(s);
};
const extractDatePart = (value) => {
  if (!value) return null;
  const s = dayjs.isDayjs(value)
    ? value.format("YYYY-MM-DD")
    : String(value instanceof Date ? value.toISOString() : value).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
};
const formatStartDateTime = (value) => {
  const d = extractDatePart(value);
  return d ? `${d}T00:00:00Z` : undefined;
};
const formatEndDateTime = (value) => {
  const d = extractDatePart(value);
  return d ? `${d}T23:59:59Z` : undefined;
};

const EditCollection = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const notify = useNotify();
  const { setPageTitle } = useCommon();
  const [languages, setLanguages] = useState([]);
  const [loading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [pinned, setPinned] = useState([]); // unified pinned items [{type, id fields, name}]
  const [activeTab, setActiveTab] = useState("details");

  const [copyPinned, setCopyPinned] = useState([]);

  // Forms
  const [detailForm] = Form.useForm();
  const [filterForm] = Form.useForm();
  const [seoForm] = Form.useForm();

  const hasCategoryOrTags = (() => {
    const categoryIds = data?.category_ids;
    const tagIds = data?.tag_ids;
    const hasCategory = categoryIds && (Array.isArray(categoryIds) ? categoryIds.length > 0 : String(categoryIds).trim().length > 0);
    const hasTags = tagIds && (Array.isArray(tagIds) ? tagIds.length > 0 : String(tagIds).trim().length > 0);
    return hasCategory || hasTags;
  })();

  console.log("hasCategoryOrTags",hasCategoryOrTags);

  const enums = useMemo(
    () => ({
      sortBy: [
        { label: "ASC", value: "ASC" },
        { label: "DESC", value: "DESC" },
      ],
      recentBy: [
        { label: "None", value: "None" },
        { label: "Upcoming", value: "Upcoming" },
        { label: "Recent", value: "Recent" },
        { label: "Trending", value: "Trending" },
        { label: "One Week", value: "One_Week" },
        { label: "One Month", value: "One_Month" },
        { label: "Three Month", value: "Three_Month" },
        { label: "Six Month", value: "Six_Month" },
        { label: "One Year", value: "One_Year" },
      ],
    }),
    []
  );

  // Fetchers for PinnedManager
  const fetchMovies = async (searchText, page = 1, perPage = 10) => {
    // Read current filter selections to forward as query params
    const fv =
      filterForm?.getFieldsValue?.([
        "movie_type_by",
        "dateRange",
        "genre_ids",
        "tag_ids",
        "language_ids",
        "rating_ids",
        "is_subscription_required",
      ]) || {};

    console.log("fv=>,", fv, filterForm);

    const dateRange = fv.dateRange;
    const movieTypeBy = fv.movie_type_by || "None";
    const releaseStart =
      movieTypeBy === "ReleaseDate"
        ? formatStartDateTime(dateRange?.[0])
        : undefined;
    const releaseEnd =
      movieTypeBy === "ReleaseDate" ? formatEndDateTime(dateRange?.[1]) : undefined;
    const expiryStart =
      movieTypeBy === "ExpiryDate"
        ? formatStartDateTime(dateRange?.[0])
        : undefined;
    const expiryEnd =
      movieTypeBy === "ExpiryDate" ? formatEndDateTime(dateRange?.[1]) : undefined;

    const params = {
      perPage: 300,
      currentPage: 1,
      searchText,
      collectionId: id,
      // backend supports arrays or csv for these ids
      genreIds: fv.genre_ids,
      tagIds: fv.tag_ids,
      languageIds: fv.language_ids,
      ratingIds: fv.rating_ids,
      isSubscriptionRequired:
        typeof fv.is_subscription_required === "boolean"
          ? fv.is_subscription_required
          : undefined,
      // date filters
      releaseStart,
      releaseEnd,
      expiryStart,
      expiryEnd,
      // default status to 'all' so both active/inactive appear unless specified otherwise
      status: "all",
    };
    // Use collectionsService endpoint tailored for pinned movies catalog
    const res = await collectionsService.getMoviesForPinned(params);
    const list = res?.data?.data?.data || res?.data?.data || [];
    const items = list.map((r) => {
      const vid = r.video_show_id || r.id || r.value;
      const title =
        r.video_show_title || r.video_show_name || r.name || `Movie #${vid}`;
      return {
        key: `movie-${vid}`,
        type: "movie",
        video_show_id: vid,
        name: title,
        label: title,
      };
    });
    const hasMore = list.length === perPage;
    return { items, hasMore };
  };

  const fetchSeries = async (searchText, page = 1, perPage = 10) => {
    // Reuse common filters (genres/tags/languages/ratings/subscription), and date by movie_type_by if applicable
    const fv =
      filterForm?.getFieldsValue?.([
        "movie_type_by",
        "dateRange",
        "genre_ids",
        "tag_ids",
        "language_ids",
        "rating_ids",
        "is_subscription_required",
      ]) || {};

    const dateRange = fv.dateRange;
    const movieTypeBy = fv.movie_type_by || "None";
    const releaseStart =
      movieTypeBy === "ReleaseDate"
        ? formatStartDateTime(dateRange?.[0])
        : undefined;
    const releaseEnd =
      movieTypeBy === "ReleaseDate" ? formatEndDateTime(dateRange?.[1]) : undefined;
    const expiryStart =
      movieTypeBy === "ExpiryDate"
        ? formatStartDateTime(dateRange?.[0])
        : undefined;
    const expiryEnd =
      movieTypeBy === "ExpiryDate" ? formatEndDateTime(dateRange?.[1]) : undefined;

    const params = {
      perPage,
      currentPage: page,
      searchText,
      collectionId: id,
      genreIds: fv.genre_ids,
      tagIds: fv.tag_ids,
      languageIds: fv.language_ids,
      ratingIds: fv.rating_ids,
      isSubscriptionRequired:
        typeof fv.is_subscription_required === "boolean"
          ? fv.is_subscription_required
          : undefined,
      releaseStart,
      releaseEnd,
      expiryStart,
      expiryEnd,
      status: "all",
    };
    // Use collectionsService endpoint tailored for pinned tv series catalog
    const res = await collectionsService.getTvSeriesForPinned(params);
    const list = res?.data?.data?.data || res?.data?.data || [];
    const items = list.map((r) => {
      const tid = r.tv_show_id || r.id || r.value;
      const name = r.tv_show_name || r.name || `TV Show #${tid}`;
      return {
        key: `tv-${tid}`,
        type: "tv_show",
        tv_show_id: tid,
        name,
        label: name,
      };
    });
    const hasMore = list.length === perPage;
    return { items, hasMore };
  };

  const fetchBanners = async (searchText, page = 1, perPage = 10) => {
    const res = await collectionsService.getBannersForPinned({
      perPage,
      currentPage: page,
      searchText,
      collectionId: id,
    });
    const list = res?.data?.data?.data || res?.data?.data || [];
    const items = list.map((r) => {
      const bid = r.m_banner_id || r.banner_id || r.id || r.value;
      const name = r.banner_name || r.name || `Banner #${bid}`;
      return {
        key: `banner-${bid}`,
        type: "banner",
        banner_id: bid,
        name,
        label: name,
      };
    });
    const hasMore = list.length === perPage;
    return { items, hasMore };
  };

  // Create-announcement modal, opened from the pinned catalog. An announcement is
  // written for the rail it appears in, so creating one without leaving the page
  // is the whole point — and on success it is pinned immediately, the way the Add
  // User modal inside Assign Subscription selects the user it just created.
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [announcementSaving, setAnnouncementSaving] = useState(false);
  const [announcementForm] = Form.useForm();
  // Set by PinnedManager so a newly created announcement can be pinned without
  // the admin having to find it in the list first.
  const [pinNewAnnouncement, setPinNewAnnouncement] = useState(null);

  const handleCreateAnnouncement = async (values) => {
    setAnnouncementSaving(true);
    try {
      const res = await announcementService.createAnnouncement({
        announcement_title: values.announcement_title?.trim(),
        announcement_subtitle: values.announcement_subtitle?.trim() || null,
        announcement_is_active: values.announcement_is_active ?? "Y",
      });
      const created = res?.data?.data;
      if (!res?.data?.status || !created) {
        notify.error({ message: res?.data?.message || "Failed to create announcement" });
        return;
      }
      notify.success({ message: res?.data?.message || "Announcement created" });
      const aid = created.m_announcement_id;
      // Pinned straight away: creating it from here means it is wanted in this
      // rail, so making the admin search for it next would be a step with no
      // decision in it.
      if (typeof pinNewAnnouncement === "function" && aid) {
        pinNewAnnouncement({
          key: `announcement-${aid}`,
          type: "announcement",
          announcement_id: aid,
          name: created.announcement_title,
          label: created.announcement_title,
          description: created.announcement_subtitle || null,
        });
      }
      announcementForm.resetFields();
      setAnnouncementModalOpen(false);
    } catch (e) {
      notify.error({
        // The api interceptor rejects with the response, so the server's message
        // is on e.data.
        message: e?.data?.message || "Failed to create announcement",
      });
    } finally {
      setAnnouncementSaving(false);
    }
  };

  // Create-spotlight modal, opened from the pinned catalog. Same idea as the
  // announcement one, with the two crops the circle rail needs: the English set
  // is uploaded here and other languages are added later in the full editor,
  // since a spotlight with no artwork renders as an empty circle.
  const [spotlightModalOpen, setSpotlightModalOpen] = useState(false);
  const [spotlightSaving, setSpotlightSaving] = useState(false);
  const [spotlightForm] = Form.useForm();
  const [spotlightVerticalImage, setSpotlightVerticalImage] = useState("");
  const [spotlightCircleImage, setSpotlightCircleImage] = useState("");

  const handleCreateSpotlight = async (values) => {
    setSpotlightSaving(true);
    try {
      const res = await spotlightService.createSpotlight({
        spotlight_title: values.spotlight_title?.trim(),
        spotlight_is_active: values.spotlight_is_active ?? "Y",
        // lang 1 is English, the end of the fallback chain — every other
        // language falls back to this set crop by crop.
        images: [
          {
            lang_id: 1,
            vertical_card_image: spotlightVerticalImage || null,
            circle_image: spotlightCircleImage || null,
          },
        ],
      });
      const created = res?.data?.data;
      if (!res?.data?.status || !created) {
        notify.error({ message: res?.data?.message || "Failed to create spotlight" });
        return;
      }
      notify.success({ message: res?.data?.message || "Spotlight created" });
      const sid = created.m_spotlight_id;
      if (typeof pinNewAnnouncement === "function" && sid) {
        pinNewAnnouncement({
          key: `spotlight-${sid}`,
          type: "spotlight",
          spotlight_id: sid,
          name: created.spotlight_title,
          label: created.spotlight_title,
        });
      }
      spotlightForm.resetFields();
      setSpotlightVerticalImage("");
      setSpotlightCircleImage("");
      setSpotlightModalOpen(false);
    } catch (e) {
      notify.error({ message: e?.data?.message || "Failed to create spotlight" });
    } finally {
      setSpotlightSaving(false);
    }
  };

  // Spotlights for the pinned catalog. Active only, for the same reason the
  // announcements fetcher filters: pinning a switched-off row puts a card in the
  // rail that the read path drops, which reads as the pin not working.
  const fetchSpotlights = async (searchText, page = 1, perPage = 10) => {
    const res = await spotlightService.getSpotlights({
      perPage,
      currentPage: page,
      searchText,
      status: "Y",
    });
    const list = res?.data?.data?.data || res?.data?.data || [];
    const items = list.map((r) => {
      const sid = r.m_spotlight_id || r.spotlight_id || r.id;
      const name = r.spotlight_title || r.spotlight_name || `Spotlight #${sid}`;
      return {
        key: `spotlight-${sid}`,
        type: "spotlight",
        spotlight_id: sid,
        name,
        label: name,
      };
    });
    return { items, hasMore: list.length === perPage };
  };

  // Announcements for the pinned catalog. Only ACTIVE ones are offered: pinning a
  // switched-off announcement would put a card in the rail that the read path
  // filters out, which reads as the pin silently not working.
  const fetchAnnouncements = async (searchText, page = 1, perPage = 10) => {
    const res = await announcementService.getAnnouncements({
      perPage,
      currentPage: page,
      searchText,
      status: "Y",
    });
    const list = res?.data?.data?.data || res?.data?.data || [];
    const items = list.map((r) => {
      const aid = r.m_announcement_id || r.announcement_id || r.id;
      const name =
        r.announcement_title || r.announcement_name || `Announcement #${aid}`;
      return {
        key: `announcement-${aid}`,
        type: "announcement",
        announcement_id: aid,
        name,
        label: name,
        // The card's second line, shown in the catalog row so two similarly
        // titled announcements are told apart before pinning.
        description: r.announcement_subtitle || null,
      };
    });
    return { items, hasMore: list.length === perPage };
  };

  const fetchCollections = async (searchText, page = 1, perPage = 10) => {
    const res = await collectionsService.getCollections({
      page,
      limit: perPage,
      search: searchText,
      status: "active",
      collection_id: id, // Pass current collection ID to exclude it from results
    });
    const list = res?.data?.data || [];
    const items = list.map((r) => ({
      key: `col-${r.collection_id}`,
      type: "collection",
      collection_id: r.collection_id,
      name: r.name || `Collection #${r.collection_id}`,
      label: r.name || `Collection #${r.collection_id}`,
    }));
    const hasMore = list.length === perPage;
    return { items, hasMore };
  };

  // Load languages
  useEffect(() => {
    setPageTitle("Edit Collection");
    const loadLanguages = async () => {
      try {
        const params = {
          perPage: 1000,
          currentPage: 1,
          searchText: "",
          status: "Y",
        };
        const res = await masterService.getVideoLanguages(params);
        const list =
          res?.data?.data?.data || res?.data?.data || res?.data || [];
        setLanguages(list);
      } catch (e) {
        notify.error({ message: "Failed to load languages" });
      }
    };
    loadLanguages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Real API-backed fetchers for Languages and Genres
  const fetchLanguages = async (searchText = "", page = 1, perPage = 10) => {
    const res = await masterService.getVideoLanguages({
      perPage,
      currentPage: page,
      searchText,
    });
    const list = res?.data?.data?.data || res?.data?.data || [];
    const items = list.map((r) => {
      const id = r.lang_id || r.id || r.value;
      const name = r.lang_name || r.name || r.label || `Language #${id}`;
      return {
        key: `lang-${id}`,
        type: "language",
        lang_id: id,
        value: id,
        name,
        label: name,
      };
    });
    const hasMore = Array.isArray(list) && list.length === perPage;
    return { items, hasMore };
  };

  const fetchGenres = async (searchText = "", page = 1, perPage = 10) => {
    const res = await masterService.getGenres({
      perPage,
      currentPage: page,
      searchText,
      status: "all",
    });
    const list = res?.data?.data?.data || res?.data?.data || [];
    const items = list.map((r) => {
      const id = r.genre_id || r.id || r.value;
      const name = r.genre_name || r.name || r.label || `Genre #${id}`;
      return {
        key: `genre-${id}`,
        type: "genre",
        genre_id: id,
        value: id,
        name,
        label: name,
      };
    });
    const hasMore = Array.isArray(list) && list.length === perPage;
    return { items, hasMore };
  };

  const fetchTags = async (searchText = "", page = 1, perPage = 10) => {
    const res = await masterService.getTagList({
      perPage,
      currentPage: page,
      searchText,
    });
    const list = res?.data?.data?.data || res?.data?.data || [];
    const items = list.map((r) => {
      const id = r.tag_id || r.id || r.value;
      const name = r.tag_name || r.name || r.label || `Tag #${id}`;
      return {
        key: `tag-${id}`,
        type: "tag",
        tag_id: id,
        value: id,
        name,
        label: name,
      };
    });
    const hasMore = Array.isArray(list) && list.length === perPage;
    return { items, hasMore };
  };

  const fetchCollectionDetails = async () => {
    try {
      const res = await collectionsService.getCollectionById(id);

      if (res?.data?.status && res.data.data) {
        const row = res.data.data;
        setData(row);
        setPinned(Array.isArray(row.sortedItem) ? row.sortedItem : []);
        // Keep forms values in sync for controlled components that rely on form instances
        detailForm.setFieldsValue({
          name: row.name,
          slug: row.slug,
          label: row.label,
          heading: row.heading,
          description: row.description,
          icon_image: row.icon_image,
          image: row.image || row.image,
          is_manual: !!row.is_manual,
          fk_template_id: row.template_relation?.temp_id,
          fk_category_id: row.categories_relation?.vc_id,
        });
        const f = row || {};
        filterForm.setFieldsValue({
          page_limit: f.page_limit ?? 5,
          sort_by: f.sort_by || "ASC",
          recent_by: f.recent_by || "Recent",
          upcoming_period: f.upcoming_period,
          is_view_all: !!f.is_view_all,
          is_auth_required:
            typeof f.is_auth_required === "boolean"
              ? f.is_auth_required
              : Array.isArray(f?.collection_filters) &&
                typeof f.collection_filters[0]?.is_auth_required === "boolean"
              ? f.collection_filters[0].is_auth_required
              : false,
          view_all_title: f.view_all_title || undefined,
          view_all_url: f.view_all_url || undefined,
          is_unique_series: !!f.is_unique_series,
          is_unique_movie: !!f.is_unique_movie,
          dateRange:
            f.dateSort?.startDate && f.dateSort?.endDate
              ? [
                  parseNaiveDateTime(f.dateSort.startDate),
                  parseNaiveDateTime(f.dateSort.endDate),
                ]
              : undefined,
          category_ids: f.category_ids,
          tag_ids: f.tag_ids,
          content_type_ids: f.content_type_ids,
          language_ids: f.language_ids,
          rating_ids: f.rating_ids,
          genre_ids: f.genre_ids,
          country_ids: f.country_ids,
          tv_series_show_ids: f.tv_series_show_ids,
          movie_ids: f.movie_ids,
        });

        // SEO form fields
        const seo = row.c_collection_seo_meta || {};
        seoForm.setFieldsValue({
          seotitle: seo.title,
          seodescription: seo.description,
          seokeyword: seo.keywords,
          slug: seo.slug,
          canonical: seo.canonical,
          image: seo.image,
          og_title: seo.og_title,
          og_description: seo.og_description,
          og_image: seo.og_image,
          og_url: seo.og_url,
          twitter_title: seo.twitter_title,
          twitter_description: seo.twitter_description,
          twitter_image: seo.twitter_image,
          facebook_title: seo.facebook_title,
          facebook_description: seo.facebook_description,
          facebook_image: seo.facebook_image,
          facebook_keywords: seo.facebook_keywords,
        });
      }
    } catch (error) {
      console.error("Error fetching collection details:", error);
      notify.error({ message: "Failed to load collection details" });
    }
  };

  useEffect(() => {
    fetchCollectionDetails();
  }, [id]);

  // Note: fetchCollectionDetails() is already invoked on [id] change above.
  // Remove duplicate effect that called fetchData to avoid double-loading.

  const onSaveDetails = async (values) => {

    try {
      setSaving(true);
      await collectionsService.updateCollection(id, {
        name: values.name,
        label: values.label || null,
        heading: values.heading || null,
        description: values.description || null,
        icon_image: values.icon_image || null,
        image: values.image || null,
        slug: values.slug || undefined,
        is_manual: values.is_manual || false,
        fk_template_id: values.fk_template_id || null,
        fk_category_id: values.fk_category_id || null,
        multilingual_data: values.multilingual_data || [],
        mscategory_list: values.category_list || [],
      });
      notify.success({ message: "Details updated successfully" });
      // Preserve current tab and fetch latest data
      const currentTab = activeTab;
      await fetchCollectionDetails();
      setActiveTab(currentTab);
    } catch (e) {
      notify.error({ message: e?.data?.message || "Failed to update details" });
    } finally {
      setSaving(false);
    }
  };

  const onSaveFilters = async (valuesOrPayload) => {
    try {
      setSaving(true);
      // When coming from CollectionFiltersForm.normalizeAndSubmit we already receive a fully-built payload
      const hasArrayPayload =
        Array.isArray(valuesOrPayload?.selectedMovies) ||
        Array.isArray(valuesOrPayload?.selectedTvSeries) ||
        Array.isArray(valuesOrPayload?.categories) ||
        Array.isArray(valuesOrPayload?.tags);

      const payload = hasArrayPayload
        ? {
            // pass-through normalized payload from child, but ensure is_manual follows Details tab toggle
            ...valuesOrPayload,
            is_manual: detailForm.getFieldValue("is_manual") || false,
          }
        : {
            // Backward compatibility: build payload from raw form values
            page_limit: valuesOrPayload.page_limit
              ? Number(valuesOrPayload.page_limit)
              : undefined,
            sort_by: valuesOrPayload.sort_by || null,
            recent_by: valuesOrPayload.recent_by || null,
            upcoming_period: valuesOrPayload.upcoming_period || null,
            from_date: valuesOrPayload.dateRange?.[0]
              ? formatStartDateTime(valuesOrPayload.dateRange[0])
              : null,
            to_date: valuesOrPayload.dateRange?.[1]
              ? formatEndDateTime(valuesOrPayload.dateRange[1])
              : null,
            is_view_all: !!valuesOrPayload.is_view_all,
            is_auth_required: !!valuesOrPayload.is_auth_required,
            view_all_title: valuesOrPayload.view_all_title || null,
            view_all_url: valuesOrPayload.view_all_url || null,
            is_unique_series: !!valuesOrPayload.is_unique_series,
            is_unique_movie: !!valuesOrPayload.is_unique_movie,
            // Denormalized CSV strings as accepted by backend
            category_ids: valuesOrPayload.category_ids || null,
            tag_ids: valuesOrPayload.tag_ids || null,
            content_type_ids: valuesOrPayload.content_type_ids || null,
            language_ids: valuesOrPayload.language_ids || null,
            rating_ids: valuesOrPayload.rating_ids || null,
            genre_ids: valuesOrPayload.genre_ids || null,
            country_ids: valuesOrPayload.country_ids || null,
            tv_series_show_ids: valuesOrPayload.tv_series_show_ids || null,
            movie_ids: valuesOrPayload.movie_ids || null,
            // New fields for banner type and movie type
            movie_type_by: valuesOrPayload.movie_type_by || "None",
            banner_type: valuesOrPayload.banner_type || "Show",
            banner_show_type: valuesOrPayload.banner_show_type || null,
            // If manual is on in details state, user can also maintain pins via this API
            is_manual: detailForm.getFieldValue("is_manual") || false,
          };

      // Debug: verify arrays presence before API call
      // selectedMovies/selectedTvSeries etc. should be arrays of { value: id }
      console.log("Filters API payload:", payload);
     
      await collectionsService.upsertFilters(id, payload);
      notify.success({ message: "Filters saved successfully" });
      // Preserve current tab and fetch latest data
      const currentTab = activeTab;
      await fetchCollectionDetails();
      setActiveTab(currentTab);
    } catch (e) {
      notify.error({ message: "Failed to save filters" });
    } finally {
      setSaving(false);
    }
  };

  const onSaveSEO = async (values) => {
    try {
      setSaving(true);
      await collectionsService.upsertSeoMeta(id, values);
      notify.success({ message: "SEO updated successfully" });
      // Preserve current tab and fetch latest data
      const currentTab = activeTab;
      await fetchCollectionDetails();
      setActiveTab(currentTab);
    } catch (e) {
      notify.error({ message: e?.data?.message || "Failed to update SEO" });
    } finally {
      setSaving(false);
    }
  };

  const detailsTab = (
    <CollectionDetailsForm
      initialValues={{
        name: data?.name,
        slug: data?.slug,
        label: data?.label,
        heading: data?.heading,
        description: data?.description,
        image: data?.image,
        icon_image: data?.icon_image,
        is_manual: !!data?.is_manual,
        fk_template_id: data?.template_relation?.temp_id,
        fk_category_id: data?.categories_relation?.vc_id,
        multilingual_data: data?.multilingual_data || [],
        mscategory_list: data?.mscategory_list || [],
      }}
      loading={saving}
      onSubmit={onSaveDetails}
      onBack={() => navigate("/collections")}
      
    />
  );

  const announcementModal = (
    <Modal
      title="Add New Announcement"
      open={announcementModalOpen}
      onCancel={() => {
        if (announcementSaving) return;
        setAnnouncementModalOpen(false);
      }}
      onOk={() => announcementForm.submit()}
      okText="Create & Pin"
      confirmLoading={announcementSaving}
      destroyOnClose
      zIndex={1100}
    >
      <Form
        form={announcementForm}
        layout="vertical"
        onFinish={handleCreateAnnouncement}
        initialValues={{ announcement_is_active: "Y" }}
      >
        <Form.Item
          label="Title"
          name="announcement_title"
          rules={[{ required: true, message: "Please enter a title" }]}
        >
          <Input placeholder="e.g. Midnight Premiere" allowClear />
        </Form.Item>
        {/* The card's whole second line, typed as it should read — separator
            included. There is no date field: "Shadow Who · 12 Jul" and
            "Till 30 Jun 2026" are both just this string. */}
        <Form.Item label="Subtitle" name="announcement_subtitle">
          <Input placeholder="e.g. Shadow Who · 12 Jul" allowClear />
        </Form.Item>
        <Form.Item label="Status" name="announcement_is_active">
          <Select
            options={[
              { label: "Active", value: "Y" },
              { label: "Inactive", value: "N" },
            ]}
          />
        </Form.Item>
      </Form>
    </Modal>
  );

  const spotlightModal = (
    <Modal
      title="Add New Spotlight"
      open={spotlightModalOpen}
      onCancel={() => {
        if (spotlightSaving) return;
        setSpotlightModalOpen(false);
      }}
      onOk={() => spotlightForm.submit()}
      okText="Create & Pin"
      confirmLoading={spotlightSaving}
      destroyOnClose
      zIndex={1100}
      width={640}
    >
      <Form
        form={spotlightForm}
        layout="vertical"
        onFinish={handleCreateSpotlight}
        initialValues={{ spotlight_is_active: "Y" }}
      >
        <Form.Item
          label="Title"
          name="spotlight_title"
          rules={[{ required: true, message: "Please enter a title" }]}
        >
          <Input placeholder="e.g. Munafik: Melangkah" allowClear />
        </Form.Item>
        {/* The English set. Other languages fall back to it crop by crop, so this
            is the one that must be filled; the rest are added in the full editor. */}
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <ImageUpload
              label="Circle Image"
              width={320}
              height={320}
              imageType="spotlights"
              value={spotlightCircleImage}
              onChange={setSpotlightCircleImage}
            />
          </Col>
          <Col xs={24} sm={12}>
            <ImageUpload
              label="Vertical Card Image"
              width={360}
              height={600}
              imageType="spotlights"
              value={spotlightVerticalImage}
              onChange={setSpotlightVerticalImage}
            />
          </Col>
        </Row>
        <Form.Item label="Status" name="spotlight_is_active">
          <Select
            options={[
              { label: "Active", value: "Y" },
              { label: "Inactive", value: "N" },
            ]}
          />
        </Form.Item>
      </Form>
    </Modal>
  );

  const pinnedTab = (
    <PinnedManager
      initialItems={pinned}
      loading={saving}
      isActive={activeTab === "pinned"}
      onSave={async (sortedItem) => {
        try {
          await collectionsService.upsertPinnedItems(id, sortedItem);
          notify.success({ message: "Pinned items saved successfully" });
          // Preserve current tab and fetch latest data
          const currentTab = activeTab;
          await fetchCollectionDetails();
          setActiveTab(currentTab);
        } catch (e) {
          notify.error({ message: "Failed to save pinned items" });
        }
      }}
      fetchAnnouncements={fetchAnnouncements}
      fetchSpotlights={fetchSpotlights}
      onCreateSpotlight={() => setSpotlightModalOpen(true)}
      onCreateAnnouncement={() => setAnnouncementModalOpen(true)}
      registerPinItem={setPinNewAnnouncement}
      fetchMovies={fetchMovies}
      fetchSeries={fetchSeries}
      fetchBanners={fetchBanners}
      fetchCollections={fetchCollections}
      fetchLanguages={fetchLanguages}
      fetchGenres={fetchGenres}
      fetchTags={fetchTags}
       hasCategoryOrTags={hasCategoryOrTags}
       setCopyPinned={setCopyPinned}
       
    />
  );

  const filtersTab = (
    <CollectionFiltersForm
      initialValues={{
        page_limit: data?.page_limit ?? 5,
        sort_by: data?.sort_by || "ASC",
        recent_by: data?.recent_by || "Recent",
        upcoming_period:
          data?.upcoming_period ??
          (Array.isArray(data?.collection_filters)
            ? data.collection_filters[0]?.upcoming_period
            : undefined) ??
          null,
        movie_type_by: data?.movie_type_by || "None",
        banner_type: data?.banner_type || "Show",
        banner_show_type:
          data?.banner_show_type ??
          (Array.isArray(data?.collection_filters)
            ? data.collection_filters[0]?.banner_show_type
            : undefined) ??
          "",
        // Default collection type for the new single-select
        collection_types:
          data?.collection_types ??
          (Array.isArray(data?.collection_filters)
            ? data.collection_filters[0]?.collection_types
            : undefined) ??
          "movie",
        // Prefill subscription requirement from data (fallback to filters[0])
        is_subscription_required:
          typeof data?.is_subscription_required === "boolean"
            ? data.is_subscription_required
            : Array.isArray(data?.collection_filters) &&
              typeof data.collection_filters[0]?.is_subscription_required ===
                "boolean"
            ? data.collection_filters[0].is_subscription_required
            : false,
        is_view_all: !!data?.is_view_all,
        is_auth_required:
          typeof data?.is_auth_required === "boolean"
            ? data.is_auth_required
            : Array.isArray(data?.collection_filters) &&
              typeof data.collection_filters[0]?.is_auth_required === "boolean"
            ? data.collection_filters[0].is_auth_required
            : false,
        view_all_title: data?.view_all_title || undefined,
        view_all_url: data?.view_all_url || undefined,
        is_unique_series: !!data?.is_unique_series,
        is_unique_movie: !!data?.is_unique_movie,
        dateRange:
          data?.dateSort?.startDate && data?.dateSort?.endDate
            ? [
                parseNaiveDateTime(data.dateSort.startDate),
                parseNaiveDateTime(data.dateSort.endDate),
              ]
            : undefined,
        category_ids: data?.category_ids,
        tag_ids: data?.tag_ids,
        content_type_ids: data?.content_type_ids,
        language_ids: data?.language_ids,
        rating_ids: data?.rating_ids,
        genre_ids: data?.genre_ids,
        sub_genre_ids: data?.sub_genre_ids,
        country_ids: data?.country_ids,
        tv_series_show_ids: data?.tv_series_show_ids,
        movie_ids: data?.movie_ids,
        // Pass through array sources so child form can extract IDs
        categories: data?.categories,
        tags: data?.tags,
        selectedContentTypes: data?.selectedContentTypes,
        selectedLanguages: data?.selectedLanguages,
        selectedRatings: data?.selectedRatings,
        selectedGenres: data?.selectedGenres,
        selectedCountries: data?.selectedCountries,
        selectedTvSeries: data?.selectedTvSeries,
        selectedMovies: data?.selectedMovies,
        mscategory_list: data?.mscategory_list || [],
      }}
      loading={saving}
      onSubmit={onSaveFilters}
      onBack={() => navigate("/collections")}
      enums={enums}
      collectionId={id}
      sortedItem={pinned}
      copyPinned={copyPinned}
    />
  );

  const seoTab = (
    <CollectionSeoForm
      initialValues={{
        c_collection_seo_meta: data?.c_collection_seo_meta,
        seotitle: data?.c_collection_seo_meta?.title,
        seodescription: data?.c_collection_seo_meta?.description,
        seokeyword: data?.c_collection_seo_meta?.keywords,
        slug: data?.c_collection_seo_meta?.slug,
        canonical: data?.c_collection_seo_meta?.canonical,
        image: data?.c_collection_seo_meta?.image,
        og_title: data?.c_collection_seo_meta?.og_title,
        og_description: data?.c_collection_seo_meta?.og_description,
        og_image: data?.c_collection_seo_meta?.og_image,
        og_url: data?.c_collection_seo_meta?.og_url,
        twitter_title: data?.c_collection_seo_meta?.twitter_title,
        twitter_description: data?.c_collection_seo_meta?.twitter_description,
        twitter_image: data?.c_collection_seo_meta?.twitter_image,
        facebook_title: data?.c_collection_seo_meta?.facebook_title,
        facebook_description: data?.c_collection_seo_meta?.facebook_description,
        facebook_image: data?.c_collection_seo_meta?.facebook_image,
        facebook_keywords: data?.c_collection_seo_meta?.facebook_keywords,
        multilingual_seo: data?.c_collection_seo_meta?.multilingual_seo,
      }}
      loading={saving}
      onSubmit={onSaveSEO}
      onBack={() => navigate("/collections")}
      languages={languages}
    />
  );

  return (
    <PageLayout
      title={
        data?.name ? `Edit Collection: ${data.name}` : `Edit Collection #${id}`
      }
      desc="Manage collection details, filters and SEO"
      avatar={user?.avatar}
    >
      {/* {JSON.stringify(filterForm.getFieldsValue())} */}
      <Card loading={loading}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: "details", label: "Details", children: detailsTab },
            { key: "filters", label: "Filters", children: filtersTab },
            {
              key: "pinned",
              label: "Pinned",
              // The modal rides with the tab it is opened from, so it mounts and
              // unmounts with the catalog rather than living at page level.
              children: (
                <>
                  {pinnedTab}
                  {announcementModal}
                  {spotlightModal}
                </>
              ),
            },
            { key: "seo", label: "SEO", children: seoTab },
          ]}
        />
      </Card>
    </PageLayout>
  );
};

export default EditCollection;
